import { isBrowser } from "@utils/browser";
import { testHost } from "@utils/config";
import { IsoDataOptionalSite } from "@utils/types";

// The browser uses the hosts in IsoData.
const hosts = !isBrowser() && {
  backendInternal: getBaseUrl(
    process.env.LEMMY_UI_BACKEND_INTERNAL ??
      process.env.LEMMY_UI_BACKEND ??
      testHost,
  ),
  backendExternal: getBaseUrl(process.env.LEMMY_UI_BACKEND ?? testHost),
};

/**
 * Optional public URL prefix, e.g. `/community`.
 * Configure with LEMMY_UI_BASE_PATH (also required at webpack build time).
 */
export function getBasePath(): string {
  // Prefer SSR isoData in the browser. Avoid importing isBrowser() here —
  // env.ts ↔ browser.ts ↔ config.ts is a circular dependency at module init.
  let raw = "";
  if (typeof window !== "undefined") {
    raw = window.isoData?.basePath || process.env.LEMMY_UI_BASE_PATH || "";
  } else {
    raw = process.env.LEMMY_UI_BASE_PATH || "";
  }
  if (!raw || raw === "/") {
    return "";
  }
  const withSlash = raw.startsWith("/") ? raw : `/${raw}`;
  return withSlash.replace(/\/+$/, "");
}

export function withBasePath(path: string): string {
  const base = getBasePath();
  if (!base) {
    return path.startsWith("/") ? path : `/${path}`;
  }
  if (!path || path === "/") {
    return base;
  }
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${base}${normalized}`;
}

export function getHttpBaseInternal() {
  if (!hosts) {
    throw new Error("Can't access internal backend host");
  }
  return hosts.backendInternal;
}

export function getBackendHostExternal() {
  return !hosts ? window.isoData.lemmyBackend : hosts.backendExternal;
}

/** True when the backend URL points at the UI server's own loopback. */
export function isLoopbackBackendHost(backendUrl: string): boolean {
  try {
    const { hostname } = new URL(backendUrl);
    return (
      hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      hostname === "::1" ||
      hostname === "[::1]"
    );
  } catch {
    return false;
  }
}

/**
 * Browser-facing Lemmy API origin.
 * If LEMMY_UI_BACKEND is missing/loopback, derive it from the public request
 * host plus LEMMY_UI_BACKEND_PUBLIC_PATH (default `/community-api`).
 */
export function resolvePublicBackendHost(requestOrigin: string): string {
  const configured = getBackendHostExternal();
  if (configured && !isLoopbackBackendHost(configured)) {
    return configured;
  }

  const rawPath = process.env.LEMMY_UI_BACKEND_PUBLIC_PATH?.trim() ?? "/community-api";
  const path =
    !rawPath || rawPath === "/"
      ? ""
      : (rawPath.startsWith("/") ? rawPath : `/${rawPath}`).replace(/\/+$/, "");

  try {
    const origin = new URL(requestOrigin).origin;
    return `${origin}${path}`;
  } catch {
    return configured;
  }
}

export function getBaseUrl(host: string) {
  const hasProtocol = /^https?:\/\//.test(host);
  const defaultProtocol =
    process.env.LEMMY_UI_HTTPS === "true" ? "https:" : "http:";
  const fullHost = hasProtocol ? host : defaultProtocol + "//" + host;
  const url = new URL(fullHost);
  const { protocol, hostname, port } = url;
  // Keep pathname so deployments like `host/community-api` keep working.
  // LemmyHttp appends `/api/v4/...` to this base URL.
  const pathname = url.pathname.replace(/\/+$/, "");
  const path = !pathname || pathname === "/" ? "" : pathname;
  return `${protocol}//${hostname}${port ? `:${port}` : ""}${path}`;
}

/** Express mount path for static assets (no base-path prefix). */
export function getStaticMountPath() {
  const hash = process.env.COMMIT_HASH || "dev";
  return `/static/${hash}`;
}

/**
 * Public URL path to static assets (includes LEMMY_UI_BASE_PATH when set).
 * Use for HTML, fetch, and img/src links. Express should use getStaticMountPath().
 */
export function getStaticDir() {
  return withBasePath(getStaticMountPath());
}

export function httpFrontendUrl(path: string, isoData: IsoDataOptionalSite) {
  return `${isoData.lemmyFrontend}${path}`;
}

export function httpBackendUrl(path: string) {
  return `${getBackendHostExternal()}${path}`;
}

export function isHttps() {
  return window.location.protocol === "https:";
}
